import React from 'react';

import { useRouter } from 'next/router';
import ArticleDetailPageView from 'src/containers/Article/Detail/ArticleDetailPage';
import { NextSeo } from 'next-seo';
import { imageUrlMaker } from 'src/utils/image-helpers';
import FAQPageJsonLd from 'src/components/FAQPageJsonLd';
import { useArticleDetailData } from 'src/hooks/use-article-detail-data';
import RouterFallback from 'src/components/RouterFallback';

interface ArticleDetailsPageContainerProps {
  slug?: string;
}

const ArticleDetailPageContainer: React.FC<
  ArticleDetailsPageContainerProps
> = ({ slug: _slug }: ArticleDetailsPageContainerProps) => {
  const router = useRouter();
  const slug = _slug ?? (router.query.slug as string);

  const { article, recentArticles, relatedArticles, loading, error } =
    useArticleDetailData(slug);

  if (!article) return null;
  const crop = article?.landscapeWideCrop;

  const imageUrl =
    article?.image?.url &&
    imageUrlMaker({
      imageUrl: article.image.url,
      cropType: 'landscapeCoverCrop',
      crop,
    });

  // Backend don't support querying length, so we slice to meet design
  const shortendRecentArticles = recentArticles?.slice?.(0, 5);
  const shortendRelatedArticles = relatedArticles?.slice?.(0, 5);

  return (
    <RouterFallback error={error} loading={loading}>
      {article && (
        <>
          <NextSeo
            title={article.metaTitle ?? article.title}
            description={article.metaDescription ?? article.summary}
            twitter={{
              cardType: 'summary_large_image',
            }}
            openGraph={{
              title: article.title,
              description: article.summary,
              images: article.image
                ? [
                    {
                      url: imageUrl ?? '',
                    },
                  ]
                : [],
            }}
          />

          <FAQPageJsonLd faq={article?.faq} />

          <ArticleDetailPageView
            article={article}
            relatedArticles={shortendRelatedArticles}
            recentArticles={shortendRecentArticles}
          />
        </>
      )}
    </RouterFallback>
  );
};

export default ArticleDetailPageContainer;
